import React from 'react';
import { MonitorIcon, MoonIcon, SettingsIcon, SunIcon } from 'lucide-react';
import type { LifeProfile, LifeStats, ViewMode } from '../types/life';
import type { ThemePreference, ThemeState } from '../hooks/useTheme';
import { cn } from '../utils/cn';
import { formatNumber, plural } from '../utils/format';
interface TopBarProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  stats: LifeStats;
  profile: LifeProfile;
  theme: ThemeState;
  onOpenSettings: () => void;
}
const TABS: Array<{
  id: ViewMode;
  label: string;
}> = [{
  id: 'now',
  label: 'Сейчас'
}, {
  id: 'life',
  label: 'Вся жизнь'
}];
const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system'
};
const THEME_LABEL: Record<ThemePreference, string> = {
  system: 'как в системе',
  light: 'светлая',
  dark: 'тёмная'
};
export function TopBar({
  view,
  onViewChange,
  stats,
  profile,
  theme,
  onOpenSettings
}: TopBarProps) {
  const ThemeIcon = theme.preference === 'system' ? MonitorIcon : theme.preference === 'light' ? SunIcon : MoonIcon;
  return <header className="border-line bg-paper flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b px-4 py-3 sm:px-6 sm:py-4" data-id="element-128" data-mp="66l1kh8tlcnu">
      <div className="flex items-center gap-4 sm:gap-8" data-id="element-129" data-mp="3okapbcsywyxq">
        <span className="text-muted hidden font-mono text-xs sm:inline" data-id="element-130" data-mp="3tjdju3bbnmrg">memento</span>
        <nav aria-label="Масштаб" className="flex items-center gap-1" data-id="element-131" data-mp="3emwafhmp0i7j">
          {TABS.map(tab => {
          const active = tab.id === view;
          return <button key={tab.id} type="button" aria-current={active ? 'page' : undefined} onClick={() => onViewChange(tab.id)} className={cn('rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ease-swift', active ? 'bg-ink text-paper' : 'text-muted hover:text-ink')} data-id="element-132" data-mp="3jjmojzext3qb">
                {tab.label}
              </button>;
        })}
        </nav>
      </div>

      <div className="flex items-center gap-3 sm:gap-5" data-id="element-133" data-mp="12cwdi42v0s8c">
        <p className="text-muted text-[11px] sm:hidden" data-id="element-134" data-mp="12fiokdoylpvb">
          осталось <span className="text-accent font-mono tabular-nums" data-id="element-135" data-mp="xf4fcsbmg9vp">{formatNumber(stats.weeksLeft)}</span> нед
          <span className="mx-1.5" data-id="element-136" data-mp="sgt3yd5ohrbd">·</span>
          <span className="font-mono tabular-nums" data-id="element-137" data-mp="1q30hwdnvgw0e">{stats.percentLived.toFixed(0)}%</span>
        </p>
        <p className="text-muted hidden text-xs sm:block" data-id="element-138" data-mp="1l3xbaos8uhqb">
          прожито <span className="text-ink font-mono tabular-nums" data-id="element-139" data-mp="1l4p7o1to0jhn">{formatNumber(stats.weeksLived)}</span>{' '}
          {plural(stats.weeksLived, ['неделя', 'недели', 'недель'])}
          <span className="mx-2" data-id="element-140" data-mp="1bdb3vhuokxip">·</span>
          осталось <span className="text-accent font-mono tabular-nums" data-id="element-141" data-mp="28thwzffs7t3a">{formatNumber(stats.weeksLeft)}</span>{' '}
          {plural(stats.weeksLeft, ['неделя', 'недели', 'недель'])}
          <span className="mx-2" data-id="element-142" data-mp="241gq691duukn">·</span>
          <span className="font-mono tabular-nums" data-id="element-143" data-mp="2be68vh0f7m7l">{stats.percentLived.toFixed(1)}%</span>
        </p>
        <div className="flex items-center gap-1" data-id="element-144" data-mp="2gchflivf5gll">
          <button type="button" onClick={() => theme.setPreference(NEXT_THEME[theme.preference])} className="text-muted hover:text-ink p-1 transition-colors duration-150 ease-swift" title={`Тема: ${THEME_LABEL[theme.preference]}`} aria-label={`Тема: ${THEME_LABEL[theme.preference]}. Переключить на ${THEME_LABEL[NEXT_THEME[theme.preference]]}`} data-id="element-145" data-mp="2gd9d4z8ktoei">
            <ThemeIcon className="h-4 w-4" data-id="element-146" data-mp="2z9ilr70plmr4" />
          </button>
          <button type="button" onClick={onOpenSettings} className="text-muted hover:text-ink p-1 transition-colors duration-150 ease-swift" aria-label={`Настройки: рождение ${profile.birthDate}, горизонт ${profile.lifeExpectancy} лет`} data-id="element-147" data-mp="343wlflmemg1l">
            <SettingsIcon className="h-4 w-4" data-id="element-148" data-mp="3i364ct1zvo99" />
          </button>
        </div>
      </div>
    </header>;
}